import { useParams } from "react-router-dom";
import { MapPin, Truck } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { fcfa, imgUrl } from "@/lib/api";
import { ProductGrid } from "@/components/ProductCard";
import { ShopCard } from "@/components/ShopCard";
import { Loading } from "@/components/RequireAuth";
import { DemoBadge, PremiumBadge } from "@/components/Badges";
import { ShopLogo } from "@/components/ShopLogo";

export function ShopsPage() {
  const { data } = useApi("/shops");
  return (
    <div data-testid="shops-page" className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <h1 className="font-display text-3xl font-black tracking-tight md:text-4xl">Boutiques</h1>
      <p className="mt-1 text-gray-500">Boutiques validées et actives sur Casadeal.</p>
      {!data ? <Loading /> : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{data.map((s) => <ShopCard key={s.id} s={s} />)}</div>
      )}
    </div>
  );
}

export default function ShopPage() {
  const { slug } = useParams();
  const { data, error } = useApi(`/shops/${slug}`);
  if (error) return <p data-testid="shop-error" className="p-10 text-center text-gray-500">{error}</p>;
  if (!data) return <Loading />;
  const { shop, products } = data;
  return (
    <div data-testid="shop-page">
      <div className="relative h-40 bg-orange-100 md:h-56">
        {shop.banner && <img src={imgUrl(shop.banner)} alt="" className="h-full w-full object-cover opacity-80" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="-mt-12 flex flex-col gap-4 md:flex-row md:items-end">
          <ShopLogo shop={shop} testid="shop-logo" className="relative h-24 w-24 rounded-2xl text-3xl ring-4 ring-white" />
          <div className="flex-1">
            <div className="flex flex-wrap gap-2">{shop.premium && <PremiumBadge />}{shop.demo && <DemoBadge />}</div>
            <h1 data-testid="shop-name" className="font-display text-3xl font-black tracking-tight">{shop.name}</h1>
            <p className="flex items-center gap-1 text-sm text-gray-500"><MapPin className="h-4 w-4" />{shop.city}</p>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-gray-700">{shop.description}</p>
        {shop.delivery?.zones?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {shop.delivery.zones.map((z) => <span key={z.name} className="flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-900"><Truck className="h-3 w-3" />{z.name} · {fcfa(z.fee, shop.currency)} · {z.delay}</span>)}
          </div>
        )}
        <h2 className="mb-4 mt-10 font-display text-2xl font-extrabold">Produits ({products.length})</h2>
        <ProductGrid items={products} testid="shop-products" />
      </div>
    </div>
  );
}
